import {Component, Inject, OnInit} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Router} from '@angular/router';
import {AuthService} from '../_services/auth.service';
import {TokenStorageService} from '../_services/token-storage.service';
import {DEFAULT_DRIVING_SCHOOL, DrivingSchool, getDrivingSchoolByHostname} from '../driving-school.config';

@Component({
  selector: 'app-login',
  template: `
    <div class="login-page">
      <section class="login-shell">
        <header class="login-topbar">
          <a class="login-brand" href="/home" aria-label="Retour a l'accueil">
            <img
              class="login-brand-logo"
              [src]="school.logoPath"
              [alt]="school.logoAlt">
          </a>

          <nav class="login-nav" aria-label="Navigation publique">
            <a href="/home">Accueil</a>
            <a href="/home#formules">Formules</a>
            <a href="/home#documents">Documents</a>
            <a [href]="'tel:' + school.phoneLink">Contact</a>
          </nav>
        </header>

        <div class="login-layout">
          <section class="login-hero">
            <span class="login-badge">Espace eleve et equipe</span>
            <h1>Connectez-vous simplement a votre espace.</h1>
            <p class="login-lead">
              Retrouvez vos informations, vos heures et le suivi de votre dossier
              depuis une page plus claire, adaptee au mobile comme au bureau.
            </p>

            <div class="login-points">
              <article class="login-point">
                <span class="login-point-icon" aria-hidden="true">✓</span>
                <div>
                  <strong>Connexion securisee</strong>
                  <p>Acces a votre espace personnel avec vos identifiants habituels.</p>
                </div>
              </article>

              <article class="login-point">
                <span class="login-point-icon" aria-hidden="true">📍</span>
                <div>
                  <strong>{{ school.name }}</strong>
                  <p>{{ fullAddress }}</p>
                </div>
              </article>

              <article class="login-point">
                <span class="login-point-icon" aria-hidden="true">☎</span>
                <div>
                  <strong>Besoin d'aide ?</strong>
                  <p>{{ school.phoneDisplay }}</p>
                </div>
              </article>
            </div>
          </section>

          <section class="login-card">
            <div class="login-card-header">
              <span class="login-card-badge">Connexion</span>
              <h2>Acceder au site</h2>
              <p>Entrez vos identifiants pour ouvrir votre espace.</p>
            </div>

            <form
              *ngIf="!isLoggedIn; else loggedInState"
              name="form"
              class="login-form"
              (ngSubmit)="f.form.valid && onSubmit()"
              #f="ngForm"
              novalidate>
              <div class="form-group">
                <label for="username">Identifiant</label>
                <input
                  id="username"
                  type="text"
                  class="form-control"
                  name="username"
                  [(ngModel)]="form.username"
                  required
                  #username="ngModel"
                  placeholder="Votre identifiant">
                <div
                  class="alert alert-danger login-alert"
                  role="alert"
                  *ngIf="username.errors && f.submitted">
                  L'identifiant est obligatoire.
                </div>
              </div>

              <div class="form-group">
                <label for="password">Mot de passe</label>
                <input
                  id="password"
                  type="password"
                  class="form-control"
                  name="password"
                  [(ngModel)]="form.password"
                  required
                  minlength="6"
                  placeholder="Votre mot de passe">
                <div
                  class="alert alert-danger login-alert"
                  role="alert"
                  *ngIf="f.submitted && (!form.password || form.password.length < 6)">
                  <div *ngIf="!form.password">Le mot de passe est obligatoire.</div>
                  <div *ngIf="form.password && form.password.length < 6">Le mot de passe doit contenir au moins 6 caracteres.</div>
                </div>
              </div>

              <button class="btn btn-primary login-submit" type="submit">
                Se connecter
              </button>

              <div
                class="alert alert-danger login-alert mb-0"
                role="alert"
                *ngIf="f.submitted && isLoginFailed">
                {{ errorMessage }}
              </div>
            </form>

            <ng-template #loggedInState>
              <div class="alert alert-success mb-0">
                Vous etes connecte.
              </div>
            </ng-template>
          </section>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .login-page {
      min-height: 100vh;
      background:
        radial-gradient(circle at 15% 10%, rgba(58, 126, 255, 0.14), transparent 28%),
        linear-gradient(180deg, #f7fbff 0%, #ffffff 38%, #f5f8fd 100%);
      color: #163047;
    }

    .login-shell {
      width: 100%;
      max-width: 1380px;
      margin: 0 auto;
      padding: 24px clamp(16px, 4vw, 48px) 48px;
    }

    .login-topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 48px;
      padding: 14px 20px;
      border: 1px solid rgba(220, 229, 239, 0.8);
      border-radius: 24px;
      background: rgba(255, 255, 255, 0.96);
      box-shadow: 0 18px 50px rgba(18, 46, 76, 0.08);
      backdrop-filter: blur(12px);
    }

    .login-brand {
      display: inline-flex;
      align-items: center;
      flex: 0 0 auto;
    }

    .login-brand-logo {
      width: auto;
      max-width: 235px;
      max-height: 67px;
      object-fit: contain;
    }

    .login-nav {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex: 1;
      gap: clamp(18px, 2.5vw, 36px);
    }

    .login-nav a {
      color: #17304a;
      font-size: 0.98rem;
      font-weight: 600;
      text-decoration: none;
      white-space: nowrap;
    }

    .login-nav a:hover {
      color: #2563eb;
      text-decoration: none;
    }

    .login-layout {
      display: grid;
      grid-template-columns: minmax(0, 1.1fr) minmax(340px, 440px);
      gap: 28px;
      align-items: start;
    }

    .login-hero,
    .login-card {
      border: 1px solid rgba(220, 228, 238, 0.7);
      border-radius: 28px;
      background: rgba(255, 255, 255, 0.96);
      box-shadow: 0 20px 50px rgba(16, 37, 58, 0.08);
    }

    .login-hero {
      padding: clamp(28px, 5vw, 56px);
    }

    .login-card {
      padding: 32px;
    }

    .login-badge,
    .login-card-badge {
      display: inline-flex;
      align-items: center;
      min-height: 36px;
      padding: 0 14px;
      margin-bottom: 16px;
      border-radius: 999px;
      background: rgba(37, 99, 235, 0.1);
      color: #2563eb;
      font-size: 0.8rem;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .login-hero h1,
    .login-card h2 {
      margin: 0 0 14px;
      color: #10253a;
      font-weight: 800;
      letter-spacing: -0.03em;
    }

    .login-hero h1 {
      max-width: 620px;
      font-size: clamp(2.6rem, 5vw, 4.2rem);
      line-height: 1;
    }

    .login-card h2 {
      font-size: 2rem;
    }

    .login-lead,
    .login-card-header p,
    .login-point p {
      color: #607289;
    }

    .login-lead {
      max-width: 620px;
      margin: 0 0 32px;
      font-size: 1.08rem;
      line-height: 1.7;
    }

    .login-points {
      display: grid;
      gap: 16px;
    }

    .login-point {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      padding: 18px;
      border: 1px solid rgba(216, 226, 240, 0.75);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.93);
    }

    .login-point-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      flex: 0 0 42px;
      border-radius: 50%;
      background: #eaf2ff;
      color: #2563eb;
      font-size: 1rem;
      font-weight: 800;
    }

    .login-point strong {
      display: block;
      margin-bottom: 4px;
      color: #10253a;
    }

    .login-point p,
    .login-card-header p {
      margin: 0;
      line-height: 1.55;
    }

    .login-card-header {
      margin-bottom: 24px;
    }

    .login-form {
      display: block;
    }

    .login-form label {
      display: block;
      margin-bottom: 8px;
      color: #10253a;
      font-weight: 600;
    }

    .login-form .form-group + .form-group {
      margin-top: 16px;
    }

    .login-form .form-control {
      min-height: 50px;
      border: 1px solid #d8e0eb;
      border-radius: 12px;
      box-shadow: none;
    }

    .login-form .form-control:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.1);
    }

    .login-submit {
      width: 100%;
      min-height: 52px;
      margin: 24px 0 16px;
      border-radius: 12px;
      font-weight: 700;
    }

    .login-alert {
      margin-top: 10px;
      margin-bottom: 0;
    }

    @media (max-width: 991.98px) {
      .login-topbar {
        flex-wrap: wrap;
        margin-bottom: 30px;
      }

      .login-nav {
        width: 100%;
        justify-content: center;
        flex-wrap: wrap;
      }

      .login-layout {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 767.98px) {
      .login-shell {
        padding: 16px 16px 36px;
      }

      .login-topbar,
      .login-hero,
      .login-card {
        border-radius: 22px;
      }

      .login-topbar {
        padding: 16px;
      }

      .login-brand-logo {
        max-width: 190px;
        max-height: 60px;
      }

      .login-nav {
        gap: 12px 18px;
        justify-content: flex-start;
      }

      .login-hero,
      .login-card {
        padding: 22px;
      }

      .login-hero h1 {
        font-size: 2.8rem;
        line-height: 1.03;
      }
    }

    @media (max-width: 479.98px) {
      .login-hero h1 {
        font-size: 2.3rem;
      }
    }
  `]
})
export class LoginComponent implements OnInit {
  form: any = {
    username: null,
    password: null
  };
  school: DrivingSchool = DEFAULT_DRIVING_SCHOOL;
  isLoggedIn = false;
  isLoginFailed = false;
  errorMessage = '';
  roles: string[] = [];

  constructor(private authService: AuthService,
              private tokenStorage: TokenStorageService,
              private router: Router,
              @Inject(DOCUMENT) private document: Document) {
  }

  ngOnInit(): void {
    this.school = getDrivingSchoolByHostname(this.document.location.hostname);

    if (this.tokenStorage.getToken()) {
      this.isLoggedIn = true;
      this.roles = this.tokenStorage.getUser().roles;
    }
  }

  get fullAddress(): string {
    return `${this.school.addressLine}, ${this.school.postalCode} ${this.school.city}`;
  }

  onSubmit(): void {
    const { username, password } = this.form;

    this.authService.login(username, password).subscribe(
      data => {
        this.tokenStorage.saveToken(data.accessToken);
        this.tokenStorage.saveUser(data);

        this.isLoginFailed = false;
        this.isLoggedIn = true;
        this.roles = this.tokenStorage.getUser().roles;
        void this.router.navigate([this.getRedirectUrl()]);
      },
      err => {
        this.errorMessage = err?.error?.message || 'Identifiants invalides.';
        this.isLoginFailed = true;
      }
    );
  }

  private getRedirectUrl(): string {
    if (this.roles.includes('ROLE_ADMIN') || this.roles.includes('ROLE_READER')) {
      return '/admin';
    }

    if (this.roles.includes('ROLE_MONITOR')) {
      return '/monitor-calendar';
    }

    if (this.roles.includes('ROLE_USER')) {
      return '/my-board';
    }

    return '/home';
  }
}
